import React, { useEffect } from "react";
import { useStore } from "../../store/store";
import noAccess from "../../util/noAccess";
import { SelectChangeEvent } from "@mui/material";
import fractionToInt from "../../util/fractionToInt";
import { comboConfigOptions } from "../../initialValues";
import { calculatePanelWidth } from "../../util/formulas/calculatePanelWidth";
import taggedWindowImages from "../../taggedImages/taggedWindowImages";
import {
  taggedConfigImages,
  taggedPicoConfigImages,
} from "../../taggedImages/taggedConfigImages";

function findNumberOfPanels(config: string) {
  if (config.includes("L") && config.includes("R")) {
    const panels = config.split("").filter((item) => !isNaN(Number(item)));
    const numOfPanels = Number(panels[0]) + Number(panels[1]);
    return numOfPanels;
  }
  const panels = config.split("").find((item) => !isNaN(Number(item)));
  const numOfPanels = Number(panels);
  return numOfPanels;
}

type TaggedImg = {
  config: string;
  image1?: string;
  image2?: string;
  image1LH?: string;
  image1RH?: string;
  image2LH?: string;
  image2RH?: string;
};

function findImage(
  option: string,
  unitType: "Door" | "Window",
  extShowSboltHandle: boolean = false,
) {
  if (!option) return "";

  let key = "image1";

  const taggedImages: TaggedImg[] =
    unitType === "Window"
      ? taggedWindowImages
      : extShowSboltHandle
        ? taggedPicoConfigImages
        : taggedConfigImages;

  var match = taggedImages.find((img) => {
    if (img.config === option) {
      key = "image1";
      return true;
    }
    const configArr = img.config.split(" OR ");
    if (configArr[0] === option) {
      key = "image1";
      return true;
    }
    if (configArr[1] === option) {
      key = "image2";
      return true;
    }
    return false;
  });
  if (!match) return "";
  if (extShowSboltHandle) {
    return match[key].replace("nano", "combo");
  }
  return match[key].replace("_Configs", "_Configs/combo");
}

function isLargePanel(config: string, frameWidth: number, material: string, unitType: string) {
  if (!frameWidth || !config) return false;

  const numOfPanels = findNumberOfPanels(config);
  const panelWidth = calculatePanelWidth({
    numberOfPanels: numOfPanels,
    material,
    exactConfig: "",
    netWidth: frameWidth,
    doorType: "Bi-fold Door/Window Combo",
    stilesAndRails: "",
    isComboWindow: unitType === "Window",
    isComboDoor: unitType === "Door",
  });

  if (panelWidth) {
    if (panelWidth > 48 || panelWidth < 18) return false;
    if (panelWidth >= 42) return `${config} (Wider Panels)`;
  }

  return config;
}

function filterMap(options: string[], overall: number, material: string, unitType: string) {
  if (!options || !options.length) return;

  let arr: string[] = [];

  options.forEach((itm) => {
    const panel = isLargePanel(itm, overall, material, unitType);

    if (panel) arr.push(panel);
  });

  return arr;
}

function useConfigSelection(
  hideHandlesWithEvenConfigs: (hide: boolean) => void,
) {
  const addToQuote = useStore((state) => state.addToQuote);
  const material = useStore((state) => state.quote.material);
  const doorConfig = useStore((state) => state.quote.exactConfig);
  const windowConfig = useStore((state) => state.quote.comboWindowConfig);

  const comboUnitLeft = useStore((state) => state.quote.comboUnitLeft);
  const comboUnitRight = useStore((state) => state.quote.comboUnitRight);

  const doorWidth = useStore((state) => state.quote.unitSize);
  const doorWidthFraction = useStore((state) => state.quote.unitSizeFraction);

  const windowWidth = useStore((state) => state.quote.comboWindowUnitSize);
  const windowWidthFraction = useStore(
    (state) => state.quote.comboWindowUnitSizeFraction,
  );

  const doorWidthInt = fractionToInt(doorWidth, doorWidthFraction);
  const windowWidthInt = fractionToInt(windowWidth || "", windowWidthFraction);

  const withExtSbolt = useStore((state) => state.quote.withExtSbolt);

  const stileCategory = useStore((state) => state.quote.stileCategory);

  const isPico = stileCategory === "Pico";

  const noExtAccess = noAccess.includes(doorConfig);

  const extShowSboltHandle = !isPico
    ? false
    : noExtAccess && withExtSbolt !== "No";

  const handleErrMsgChange = useStore((state) => state.handleError);

  const configOptionsDoor = comboConfigOptions({
    width: doorWidthInt,
    isWindow: false,
    side: comboUnitLeft === "Door" ? "L" : "R",
  });
  const configOptionsWindow = comboConfigOptions({
    width: windowWidthInt,
    isWindow: true,
    side: comboUnitLeft === "Window" ? "L" : "R",
  });

  const optionsDoor = filterMap(
    configOptionsDoor?.length ? configOptionsDoor : [],
    doorWidthInt,
    material,
    "Door"
  );

  const optionsWindow = filterMap(
    configOptionsWindow?.length ? configOptionsWindow : [],
    windowWidthInt,
    material,
    "Window"
  );

  const doorImg = findImage(doorConfig, "Door", extShowSboltHandle);
  const windowImg = findImage(windowConfig || "", "Window");

  const handleSboltExtErr = (config: string) => {
    if (isPico && noAccess.includes(config) && !withExtSbolt) {
      return handleErrMsgChange(
        "withExtSboltErr",
        "Select an Exterior Access Option",
      );
    }

    handleErrMsgChange("withExtSboltErr", "");
  };

  const handleChangeDoor = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    let update = {
      exactConfig: value,
      stilesAndRails: "",
      stileCategory: "",
    };

    const hideHandles = noAccess.includes(value);

    if (hideHandles) {
      update["withHandle"] = "";
      update["handleStyle"] = "";
      update["handleColor"] = "";
      update["magnetCatch"] = "";
    }

    if (isPico) {
      handleSboltExtErr(value);
    }

    addToQuote(update);
    hideHandlesWithEvenConfigs(hideHandles);
  };

  const handleChangeWindow = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    let update = {
      comboWindowConfig: value,
      stilesAndRails: "",
      stileCategory: "",
    };

    addToQuote(update);
  };

  const handleChangeSboltExt = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    addToQuote({ withExtSbolt: value });
    handleErrMsgChange("withExtSboltErr", "");
  };

  useEffect(() => {
    if (!doorWidthInt && !windowWidthInt) return;

    let update = {};

    if (
      optionsDoor?.length && 
      !optionsDoor?.some(opt => opt.replace(" (Wider Panels)", "") === (doorConfig || ""))
    ) {
      update["exactConfig"] = "";
    }

    if (
      optionsWindow?.length && 
      !optionsWindow?.some(opt => opt.replace(" (Wider Panels)", "") === (windowConfig || ""))
    ) {
      update["comboWindowConfig"] = "";
    }

    addToQuote(update);
  }, [doorWidthInt, windowWidthInt]);

  useEffect(() => {
    if (!doorImg || !windowImg) return;

    addToQuote({
      exactConfigImg: doorImg,
      exactConfigImgRightCorner: windowImg,
    });
  }, [doorImg, windowImg]);

  const getConfigVal = (unitType?: string) => {
    if (!unitType) return "";

    if (unitType === "Door") {
      return doorConfig || "";
    }

    return windowConfig || "";
  };

  const getChangeFn = (unitType?: string) => {
    if (!unitType) return () => null;

    if (unitType === "Door") {
      return handleChangeDoor;
    }

    return handleChangeWindow;
  };

  const getOptions = (unitType?: string) => {
    if (!unitType) return [];

    if (unitType === "Door") {
      return optionsDoor?.length ? optionsDoor : [];
    }

    return optionsWindow?.length ? optionsWindow : [];
  };

  const getImg = (unitType?: string) => {
    if (!unitType) return "";

    if (unitType === "Door") {
      return doorImg || "";
    }

    return windowImg || "";
  };

  return {
    comboUnitLeft,
    comboUnitRight,
    getConfigVal,
    getChangeFn,
    getOptions,
    getImg,
    isPico,
    withExtSbolt,
    noExtAccess,
    handleChangeSboltExt,
  };
}

export default useConfigSelection;
